import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { updateSupplier } from '../../../../redux/services/suppliersService';
import { updateUserSupplierDetails } from '../../../../redux/globalSlices/authSlice';
import { DillField, DillSelect, DillInput, DillButton } from '../../../general/Dill';
import { formatMoney } from '@dill/dill-shared';

// ─── Types ────────────────────────────────────────────────────────────────────

type ParameterKey =
  | 'yearsInBusiness'
  | 'creditSafeScore'
  | 'creditSafeLimit'
  | 'tradeReferences'
  | 'bankBalance'
  | 'paymentHistory';

type Breakpoint = { minValue: string; points: string };

type ScorecardParam = {
  id: string;
  key: ParameterKey;
  weight: string;
  breakpoints: Breakpoint[];
  expanded: boolean;
};

type Tier = { id: string; minScore: string; label: string; creditLimit: string };

type HardGate = { id: string; key: ParameterKey; operation: string; value: string };

// ─── Constants ────────────────────────────────────────────────────────────────

const PARAMETER_OPTIONS: { label: string; value: ParameterKey; unit: string }[] = [
  { label: 'Years in business', value: 'yearsInBusiness', unit: 'years' },
  { label: 'CreditSafe score', value: 'creditSafeScore', unit: '0–100' },
  { label: 'CreditSafe suggested limit', value: 'creditSafeLimit', unit: '$' },
  { label: 'Positive trade references', value: 'tradeReferences', unit: 'count' },
  { label: 'Bank balance (Plaid)', value: 'bankBalance', unit: '$' },
  { label: 'Payment history score', value: 'paymentHistory', unit: '0–100' },
];

const OPERATION_OPTIONS = [
  { label: '> Greater than', value: '>' },
  { label: '>= Greater than or equal', value: '>=' },
  { label: '< Less than', value: '<' },
  { label: '<= Less than or equal', value: '<=' },
];

const DEFAULT_PARAMS: Omit<ScorecardParam, 'id'>[] = [
  {
    key: 'yearsInBusiness',
    weight: '20',
    expanded: false,
    breakpoints: [
      { minValue: '0', points: '0' },
      { minValue: '1', points: '5' },
      { minValue: '3', points: '10' },
      { minValue: '6', points: '15' },
      { minValue: '11', points: '20' },
    ],
  },
  {
    key: 'creditSafeScore',
    weight: '25',
    expanded: false,
    breakpoints: [
      { minValue: '0', points: '0' },
      { minValue: '40', points: '8' },
      { minValue: '60', points: '15' },
      { minValue: '75', points: '20' },
      { minValue: '90', points: '25' },
    ],
  },
  {
    key: 'tradeReferences',
    weight: '20',
    expanded: false,
    breakpoints: [
      { minValue: '0', points: '0' },
      { minValue: '1', points: '7' },
      { minValue: '2', points: '13' },
      { minValue: '3', points: '20' },
    ],
  },
  {
    key: 'bankBalance',
    weight: '15',
    expanded: false,
    breakpoints: [
      { minValue: '0', points: '0' },
      { minValue: '10000', points: '5' },
      { minValue: '50000', points: '10' },
      { minValue: '200000', points: '15' },
    ],
  },
  {
    key: 'paymentHistory',
    weight: '12',
    expanded: false,
    breakpoints: [
      { minValue: '0', points: '0' },
      { minValue: '25', points: '4' },
      { minValue: '50', points: '8' },
      { minValue: '75', points: '12' },
    ],
  },
  {
    key: 'creditSafeLimit',
    weight: '8',
    expanded: false,
    breakpoints: [
      { minValue: '0', points: '0' },
      { minValue: '5000', points: '3' },
      { minValue: '25000', points: '5' },
      { minValue: '75000', points: '8' },
    ],
  },
];

const DEFAULT_TIERS: Omit<Tier, 'id'>[] = [
  { minScore: '75', label: 'Tier A', creditLimit: '15000' },
  { minScore: '60', label: 'Tier B', creditLimit: '7500' },
  { minScore: '40', label: 'Tier C', creditLimit: '3000' },
  { minScore: '0', label: 'Decline', creditLimit: '' },
];

// ─── Local uid (for React keys only, not persisted) ───────────────────────────

let scorecardUid = 0;
const nextId = () => String(++scorecardUid);

// ─── Small helpers ────────────────────────────────────────────────────────────

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4h6v2" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const DeleteBtn = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="w-7 h-7 flex items-center justify-center border border-red-200 rounded-md bg-red-50 text-red-500 hover:bg-red-100 transition-colors shrink-0">
    <TrashIcon />
  </button>
);

const Section = ({
  title,
  subtitle,
  children,
  badge,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) => (
  <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
      <div>
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {badge}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

function scoreParam(value: number, breakpoints: Breakpoint[]): number {
  const sorted = [...breakpoints]
    .map((b) => ({ min: parseFloat(b.minValue), pts: parseFloat(b.points) }))
    .filter((b) => !isNaN(b.min) && !isNaN(b.pts))
    .sort((a, b) => a.min - b.min);
  let earned = 0;
  for (const bp of sorted) {
    if (value >= bp.min) earned = bp.pts;
  }
  return earned;
}

// ─── Helpers: convert saved ↔ local ──────────────────────────────────────────

type SavedParam = {
  key: string;
  weight: string;
  breakpoints: { minValue: string; points: string }[];
};
type SavedTier = { minScore: string; label: string; creditLimit: string };
type SavedGate = { key: string; operation: string; value: string };

function toLocalParams(saved: SavedParam[] | null | undefined): ScorecardParam[] {
  if (!saved?.length) return DEFAULT_PARAMS.map((p) => ({ ...p, id: nextId() }));
  return saved.map((p) => ({
    id: nextId(),
    key: p.key as ParameterKey,
    weight: p.weight,
    expanded: false,
    breakpoints: p.breakpoints,
  }));
}

function toLocalTiers(saved: SavedTier[] | null | undefined): Tier[] {
  if (!saved?.length) return DEFAULT_TIERS.map((t) => ({ ...t, id: nextId() }));
  return saved.map((t) => ({ ...t, id: nextId() }));
}

function toLocalGates(saved: SavedGate[] | null | undefined): HardGate[] {
  if (!saved?.length) return [];
  return saved.map((g) => ({ ...g, id: nextId(), key: g.key as ParameterKey }));
}

function serializeParams(params: ScorecardParam[]) {
  return params.map(({ key, weight, breakpoints }) => ({ key, weight, breakpoints }));
}
function serializeTiers(tiers: Tier[]) {
  return tiers.map(({ minScore, label, creditLimit }) => ({ minScore, label, creditLimit }));
}
function serializeGates(gates: HardGate[]) {
  return gates.map(({ key, operation, value }) => ({ key, operation, value }));
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ScorecardSettings() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const saved = currentSupplier?.creditApplicationSetting?.scorecardSettings;

  const [params, setParams] = useState<ScorecardParam[]>(() => toLocalParams(saved?.params));
  const [tiers, setTiers] = useState<Tier[]>(() => toLocalTiers(saved?.tiers));
  const [hardGates, setHardGates] = useState<HardGate[]>(() => toLocalGates(saved?.hardGates));
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setParams(toLocalParams(saved?.params));
    setTiers(toLocalTiers(saved?.tiers));
    setHardGates(toLocalGates(saved?.hardGates));
  }, [JSON.stringify(saved)]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dirty detection ───────────────────────────────────────────────────────

  const isDirty = useMemo(() => {
    const currentParams = JSON.stringify(serializeParams(params));
    const currentTiers = JSON.stringify(serializeTiers(tiers));
    const currentGates = JSON.stringify(serializeGates(hardGates));
    const savedParams = JSON.stringify(serializeParams(toLocalParams(saved?.params)));
    const savedTiers = JSON.stringify(serializeTiers(toLocalTiers(saved?.tiers)));
    const savedGates = JSON.stringify(serializeGates(toLocalGates(saved?.hardGates)));
    return (
      saved == null ||
      currentParams !== savedParams ||
      currentTiers !== savedTiers ||
      currentGates !== savedGates
    );
  }, [params, tiers, hardGates, saved]);

  // ── Live score ────────────────────────────────────────────────────────────

  const totalWeight = params.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
  const weightOk = Math.abs(totalWeight - 100) < 0.1;

  const liveScore = useMemo(() => {
    let total = 0;
    const breakdown: { param: ScorecardParam; earned: number; max: number }[] = [];
    for (const p of params) {
      const max = parseFloat(p.weight) || 0;
      const val = parseFloat(previewValues[p.id] ?? '0') || 0;
      const earned = Math.min(scoreParam(val, p.breakpoints), max);
      total += earned;
      breakdown.push({ param: p, earned, max });
    }
    return { total: Math.round(total * 10) / 10, breakdown };
  }, [params, previewValues]);

  const liveTier = useMemo(() => {
    const sorted = [...tiers]
      .map((t) => ({ ...t, min: parseFloat(t.minScore) || 0 }))
      .sort((a, b) => b.min - a.min);
    return sorted.find((t) => liveScore.total >= t.min) ?? null;
  }, [tiers, liveScore.total]);

  const liveGateFired = useMemo(() => {
    for (const gate of hardGates) {
      const val = parseFloat(previewValues[`gate_${gate.id}`] ?? '0') || 0;
      const threshold = parseFloat(gate.value) || 0;
      const fires =
        gate.operation === '>'
          ? val > threshold
          : gate.operation === '>='
          ? val >= threshold
          : gate.operation === '<'
          ? val < threshold
          : val <= threshold;
      if (fires) return gate;
    }
    return null;
  }, [hardGates, previewValues]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const paramLabel = (key: ParameterKey) =>
    PARAMETER_OPTIONS.find((p) => p.value === key)?.label ?? key;
  const paramUnit = (key: ParameterKey) =>
    PARAMETER_OPTIONS.find((p) => p.value === key)?.unit ?? '';

  const availableParamOptions = PARAMETER_OPTIONS.filter(
    (opt) => !params.some((p) => p.key === opt.value)
  );

  const updParam = (id: string, u: Partial<ScorecardParam>) =>
    setParams((p) => p.map((x) => (x.id === id ? { ...x, ...u } : x)));

  const addParam = () => {
    const next = availableParamOptions[0];
    if (!next) return;
    setParams((p) => [
      ...p,
      {
        id: nextId(),
        key: next.value as ParameterKey,
        weight: '10',
        expanded: true,
        breakpoints: [{ minValue: '0', points: '0' }],
      },
    ]);
  };

  const updBreakpoint = (paramId: string, bpIdx: number, u: Partial<Breakpoint>) =>
    setParams((p) =>
      p.map((x) =>
        x.id === paramId
          ? { ...x, breakpoints: x.breakpoints.map((b, i) => (i === bpIdx ? { ...b, ...u } : b)) }
          : x
      )
    );

  const addBreakpoint = (paramId: string) =>
    setParams((p) =>
      p.map((x) =>
        x.id === paramId
          ? { ...x, breakpoints: [...x.breakpoints, { minValue: '', points: '' }] }
          : x
      )
    );

  const removeBreakpoint = (paramId: string, bpIdx: number) =>
    setParams((p) =>
      p.map((x) =>
        x.id === paramId ? { ...x, breakpoints: x.breakpoints.filter((_, i) => i !== bpIdx) } : x
      )
    );

  const updTier = (id: string, u: Partial<Tier>) =>
    setTiers((t) => t.map((x) => (x.id === id ? { ...x, ...u } : x)));

  const addGate = () =>
    setHardGates((g) => [
      ...g,
      { id: nextId(), key: 'yearsInBusiness', operation: '<', value: '' },
    ]);

  const updGate = (id: string, u: Partial<HardGate>) =>
    setHardGates((g) => g.map((x) => (x.id === id ? { ...x, ...u } : x)));

  const tierColor = (creditLimit: string, idx: number) => {
    if (!creditLimit) return 'bg-red-50 border-red-200 text-red-700';
    const colors = [
      'bg-green-50 border-green-200 text-green-700',
      'bg-blue-50 border-blue-200 text-blue-700',
      'bg-yellow-50 border-yellow-200 text-yellow-700',
      'bg-orange-50 border-orange-200 text-orange-700',
    ];
    return colors[idx % colors.length];
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!weightOk) e.weight = `Total weight is ${totalWeight.toFixed(0)}% — must equal 100%`;
    tiers.forEach((t, i) => {
      if (t.minScore === '') e[`tier-${i}-score`] = 'Required';
      if (!t.label) e[`tier-${i}-label`] = 'Required';
    });
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!currentSupplier?.id || !isDirty || !validate()) return;
    setIsSaving(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: currentSupplier.id,
        supplierDetails: {
          creditApplicationSetting: {
            ...currentSupplier.creditApplicationSetting,
            scorecardSettings: {
              params: serializeParams(params),
              tiers: serializeTiers(tiers),
              hardGates: serializeGates(hardGates),
            },
          },
        },
      })
    );
    if (updateSupplier.fulfilled.match(result) && result.payload?.success) {
      dispatch(
        updateUserSupplierDetails({
          creditApplicationSetting: {
            ...currentSupplier.creditApplicationSetting,
            scorecardSettings: {
              params: serializeParams(params),
              tiers: serializeTiers(tiers),
              hardGates: serializeGates(hardGates),
            },
          },
        })
      );
    }
    setIsSaving(false);
  };

  const saveBtn = (
    <DillButton
      onClick={handleSave}
      label={isSaving ? 'Saving…' : 'Save'}
      variant="primary"
      isDisabled={!isDirty || isSaving}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {/* ── Hard-Gate Rules ── */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Hard-gate rules</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Absolute disqualifiers — these override the scorecard regardless of score
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
              Optional
            </span>
            {saveBtn}
          </div>
        </div>
        <div className="p-5 flex flex-col gap-3">
          {hardGates.length === 0 && (
            <p className="text-xs text-gray-400 italic">
              No hard-gate rules configured. Scorecard alone determines the suggestion.
            </p>
          )}
          {hardGates.map((gate) => (
            <div
              key={gate.id}
              className="flex gap-3 items-end bg-orange-50 border border-orange-100 rounded-lg px-4 py-3">
              <DillField label="Parameter" className="flex-1">
                <DillSelect
                  value={gate.key}
                  onChange={(v) => updGate(gate.id, { key: v as ParameterKey })}
                  options={PARAMETER_OPTIONS.map((p) => ({ label: p.label, value: p.value }))}
                />
              </DillField>
              <DillField label="Condition" className="w-64">
                <DillSelect
                  value={gate.operation}
                  onChange={(v) => updGate(gate.id, { operation: v })}
                  options={OPERATION_OPTIONS}
                />
              </DillField>
              <DillField label={`Value (${paramUnit(gate.key)})`} className="flex-1">
                <DillInput
                  type="number"
                  value={gate.value}
                  onChange={(v) => updGate(gate.id, { value: v })}
                  placeholder="threshold"
                />
              </DillField>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-red-600 bg-red-100 border border-red-200 rounded px-2 py-1">
                  → Decline
                </span>
                <DeleteBtn onClick={() => setHardGates((g) => g.filter((x) => x.id !== gate.id))} />
              </div>
            </div>
          ))}
          <DillButton
            variant="secondary"
            label="Add hard-gate rule"
            icon={<PlusIcon />}
            onClick={addGate}
          />
        </div>
      </div>

      {/* ── Scorecard Parameters ── */}
      <Section
        title="Scorecard — parameters & weights"
        subtitle="Each parameter earns points based on its scoring breakpoints. Weights must total 100%."
        badge={
          <div className="flex items-center gap-3">
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                weightOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}>
              {totalWeight.toFixed(0)}% / 100%
            </span>
            {saveBtn}
          </div>
        }>
        {errors.weight && (
          <div className="mb-3 text-xs text-red-500 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
            {errors.weight}
          </div>
        )}
        {/* Weight bar */}
        <div className="mb-4 h-2 rounded-full bg-gray-100 overflow-hidden flex">
          {params.map((p, i) => {
            const w = parseFloat(p.weight) || 0;
            const hues = [
              'bg-blue-400',
              'bg-green-400',
              'bg-purple-400',
              'bg-yellow-400',
              'bg-orange-400',
              'bg-pink-400',
            ];
            return w > 0 ? (
              <div
                key={p.id}
                className={`h-full ${hues[i % hues.length]}`}
                style={{ width: `${w}%` }}
                title={`${paramLabel(p.key)}: ${w}%`}
              />
            ) : null;
          })}
        </div>
        <div className="flex flex-col gap-3">
          {params.map((p) => (
            <div key={p.id} className="border border-gray-100 rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
                <div className="flex-1">
                  <DillField label="Parameter">
                    <DillSelect
                      value={p.key}
                      onChange={(v) => updParam(p.id, { key: v as ParameterKey })}
                      options={[
                        { label: paramLabel(p.key), value: p.key },
                        ...availableParamOptions.map((o) => ({ label: o.label, value: o.value })),
                      ]}
                    />
                  </DillField>
                </div>
                <div className="w-28">
                  <DillField label="Weight (%)">
                    <DillInput
                      type="number"
                      value={p.weight}
                      onChange={(v) => updParam(p.id, { weight: v })}
                      placeholder="0"
                    />
                  </DillField>
                </div>
                <button
                  onClick={() => updParam(p.id, { expanded: !p.expanded })}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                  <span>{p.breakpoints.length} breakpoints</span>
                  <ChevronIcon open={p.expanded} />
                </button>
                <DeleteBtn onClick={() => setParams((prev) => prev.filter((x) => x.id !== p.id))} />
              </div>
              {p.expanded && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-2">
                    If value ≥ threshold → earn points &nbsp;·&nbsp; unit: {paramUnit(p.key)}
                  </p>
                  <div className="flex flex-col gap-2">
                    {p.breakpoints.map((bp, bpIdx) => (
                      <div key={bpIdx} className="flex items-center gap-2">
                        <DillInput
                          type="number"
                          value={bp.minValue}
                          onChange={(v) => updBreakpoint(p.id, bpIdx, { minValue: v })}
                          placeholder="min value"
                        />
                        <span className="text-xs text-gray-400">→</span>
                        <DillInput
                          type="number"
                          value={bp.points}
                          onChange={(v) => updBreakpoint(p.id, bpIdx, { points: v })}
                          placeholder="pts"
                        />
                        <span className="text-[10px] text-gray-400">pts</span>
                        {p.breakpoints.length > 1 && (
                          <DeleteBtn onClick={() => removeBreakpoint(p.id, bpIdx)} />
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addBreakpoint(p.id)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium w-fit">
                      <PlusIcon />
                      <span>Add breakpoint</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {availableParamOptions.length > 0 && (
            <DillButton
              variant="secondary"
              label="Add parameter"
              icon={<PlusIcon />}
              onClick={addParam}
            />
          )}
        </div>
      </Section>

      {/* ── Score → Credit Limit Tiers ── */}
      <Section
        title="Score → credit limit tiers"
        subtitle="Map score ranges to credit limit suggestions"
        badge={saveBtn}>
        <div className="flex flex-col gap-3 w-full">
          {/* Tier cards */}
          <div className="flex gap-2 mb-2">
            {tiers.map((t, i) => (
              <div
                key={t.id}
                className={`border rounded-lg px-3 py-2 text-center min-w-20 w-full ${tierColor(
                  t.creditLimit,
                  i
                )}`}>
                <p className="text-xs font-semibold">{t.label || `Tier ${i + 1}`}</p>
                <p className="text-sm font-bold mt-0.5">
                  {t.creditLimit ? formatMoney(parseFloat(t.creditLimit) || 0, 2) : 'Decline'}
                </p>
                <p className="text-[10px] opacity-70 mt-0.5">≥ {t.minScore || '—'} pts</p>
              </div>
            ))}
          </div>
          {tiers.map((t, i) => (
            <div
              key={t.id}
              className="flex gap-3 items-end bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
              <DillField label="Minimum score Points (0-100)" className="flex-1">
                {errors[`tier-${i}-score`] && (
                  <p className="text-[10px] text-red-500 mb-1">{errors[`tier-${i}-score`]}</p>
                )}
                <DillInput
                  type="number"
                  value={t.minScore}
                  onChange={(v) => updTier(t.id, { minScore: v })}
                  placeholder="0"
                />
              </DillField>
              <DillField label="Label" className="flex-1">
                {errors[`tier-${i}-label`] && (
                  <p className="text-[10px] text-red-500 mb-1">{errors[`tier-${i}-label`]}</p>
                )}
                <DillInput
                  value={t.label}
                  onChange={(v) => updTier(t.id, { label: v })}
                  placeholder="Tier A"
                />
              </DillField>
              <DillField label="Credit limit ($)" className="flex-1">
                <DillInput
                  type="number"
                  value={t.creditLimit}
                  onChange={(v) => updTier(t.id, { creditLimit: v })}
                  placeholder="Leave blank to decline"
                />
              </DillField>
              <DeleteBtn onClick={() => setTiers((prev) => prev.filter((x) => x.id !== t.id))} />
            </div>
          ))}
          <DillButton
            variant="secondary"
            label="Add tier"
            icon={<PlusIcon />}
            onClick={() =>
              setTiers((prev) => [
                ...prev,
                { id: nextId(), minScore: '', label: '', creditLimit: '' },
              ])
            }
          />
        </div>
      </Section>

      {/* ── Live Preview ── */}
      <Section
        title="Live preview"
        subtitle="Test the scorecard with sample values"
        badge={
          <div
            className={`text-xs font-semibold border rounded-lg px-3 py-1.5 ${
              liveGateFired
                ? 'bg-red-50 border-red-200 text-red-700'
                : liveTier?.creditLimit
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
            {liveGateFired ? (
              <span>Hard gate fired → Decline</span>
            ) : liveTier ? (
              <span>
                {liveTier.label}:{' '}
                {liveTier.creditLimit
                  ? formatMoney(parseFloat(liveTier.creditLimit), 2)
                  : 'Decline'}
              </span>
            ) : (
              <span>—</span>
            )}
          </div>
        }>
        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-end justify-between mb-1.5">
              <span className="text-xs text-gray-500">Score</span>
              <span className="text-2xl font-bold text-gray-900 leading-none">
                {liveScore.total.toFixed(1)}
                <span className="text-sm font-normal text-gray-400"> / 100</span>
              </span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  liveScore.total >= 75
                    ? 'bg-green-400'
                    : liveScore.total >= 60
                    ? 'bg-blue-400'
                    : liveScore.total >= 40
                    ? 'bg-yellow-400'
                    : 'bg-red-400'
                }`}
                style={{ width: `${Math.min(liveScore.total, 100)}%` }}
              />
            </div>
            <div className="relative mt-1 h-4">
              {tiers.map((t) => {
                const pct = Math.min(parseFloat(t.minScore) || 0, 100);
                return (
                  <div
                    key={t.id}
                    className="absolute flex flex-col items-center"
                    style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}>
                    <div className="w-px h-2 bg-gray-300" />
                    <span className="text-[9px] text-gray-400 whitespace-nowrap">{t.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {liveScore.breakdown.map(({ param, earned, max }) => (
              <div key={param.id} className="flex items-center gap-3">
                <div className="w-40 shrink-0">
                  <p className="text-xs text-gray-600 font-medium truncate">
                    {paramLabel(param.key)}
                  </p>
                  <p className="text-[10px] text-gray-400">unit: {paramUnit(param.key)}</p>
                </div>
                <DillInput
                  type="number"
                  value={previewValues[param.id] ?? ''}
                  onChange={(v) => setPreviewValues((pv) => ({ ...pv, [param.id]: v }))}
                  placeholder="test value"
                />
                <div className="flex-1 w-64">
                  <div className="flex items-center justify-between mb-1 w-64">
                    <span className="text-[10px] text-gray-400">
                      {earned.toFixed(1)} / {max} pts
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-400 rounded-full transition-all"
                      style={{ width: max > 0 ? `${(earned / max) * 100}%` : '0%' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {hardGates.length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-semibold text-orange-700 mb-2">Hard-gate test values</p>
              {hardGates.map((gate) => (
                <div key={gate.id} className="flex items-center gap-3 mb-2">
                  <span className="text-xs text-gray-600 w-40 shrink-0">
                    {paramLabel(gate.key)}
                  </span>
                  <DillInput
                    type="number"
                    value={previewValues[`gate_${gate.id}`] ?? ''}
                    onChange={(v) => setPreviewValues((pv) => ({ ...pv, [`gate_${gate.id}`]: v }))}
                    placeholder="test value"
                  />
                  <div className="w-64">
                    <span className="text-xs text-gray-400">
                      {gate.operation} {gate.value} → Decline
                    </span>
                    {previewValues[`gate_${gate.id}`] !== undefined &&
                      (() => {
                        const val = parseFloat(previewValues[`gate_${gate.id}`] ?? '0') || 0;
                        const threshold = parseFloat(gate.value) || 0;
                        const fires =
                          gate.operation === '>'
                            ? val > threshold
                            : gate.operation === '>='
                            ? val >= threshold
                            : gate.operation === '<'
                            ? val < threshold
                            : val <= threshold;
                        return fires ? (
                          <span className="text-[10px] font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                            FIRES
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                            PASS
                          </span>
                        );
                      })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
